import {Component, Inject, Input} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import {validators} from '@emma-labs/shared/angular';
import {
  areNewAndOldQuestionsIdentical,
  ENVIRONMENT,
  EnvironmentBroker,
  HealthAnswers,
  QuestionnaireId,
  QuestionnaireStatus,
} from '@emma-labs/shared/typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {CardModule} from 'primeng/card';
import {DividerModule} from 'primeng/divider';
import {debounceTime} from 'rxjs';
import {distinctUntilChanged} from 'rxjs/operators';
import {ExtraQuestionnaire} from '../../../entities/proposal-page-object/proposal-page-object';
import {DEBOUNCE_TIME} from '../../../utils/utility-constants';
import {BaseSectionComponent} from '../../base-section/base-section.component';
import {QuestionComponent} from '../../question/question.component';
import {
  EXTRA_QUESTIONNAIRE_DETAILS_EN_TEMPLATE,
  EXTRA_QUESTIONNAIRE_DETAILS_FR_TEMPLATE,
  EXTRA_QUESTIONNAIRE_DETAILS_TEMPLATE_PROMPTS,
} from './details-message-templates';

interface QuestionnaireForm {
  [key: string]: AbstractControl<unknown, unknown>;
}

function hasNoInputPrompts(input: AbstractControl): ValidationErrors | null {
  return EXTRA_QUESTIONNAIRE_DETAILS_TEMPLATE_PROMPTS.some((prompt) => input.value?.includes(prompt))
    ? {hasNoInputPrompts: true}
    : null;
}

@UntilDestroy()
@Component({
  selector: 'em-extra-questionnaires-section',
  templateUrl: './extra-questionnaires-section.component.html',
  imports: [ReactiveFormsModule, CardModule, QuestionComponent, DividerModule],
})
export class ExtraQuestionnairesSectionComponent extends BaseSectionComponent<
  FormArray<FormGroup<QuestionnaireForm>>,
  ExtraQuestionnaire[],
  HealthAnswers
> {
  readonly QUESTIONNAIRE_STATUS = QuestionnaireStatus;

  extraQuestionnaires: ExtraQuestionnaire[] = [];

  @Input({required: true})
  override set props(extraQuestionnaires: ExtraQuestionnaire[] | undefined) {
    if (!extraQuestionnaires) {
      this.extraQuestionnaires = [];
      return;
    }

    if (extraQuestionnaires.length !== this.extraQuestionnaires.length) {
      this.buildForm(extraQuestionnaires);
      this.extraQuestionnaires = extraQuestionnaires;
      return;
    }

    if (!this.areNewAndOldExtraQuestionnairesIdentical(extraQuestionnaires, this.extraQuestionnaires)) {
      this.updateForm(extraQuestionnaires);
    }
    this.extraQuestionnaires.forEach((eq, i) => {
      eq.status = extraQuestionnaires[i].status;
    });
  }

  constructor(
    private readonly fb: FormBuilder,
    @Inject(ENVIRONMENT)
    environment: EnvironmentBroker,
  ) {
    super(environment);
    this.form = this.createForm();
  }

  override formToOutput(): HealthAnswers {
    const healthAnswers: HealthAnswers = {};
    this.form.controls.forEach((fg) => {
      Object.entries(fg.getRawValue()).forEach(([prop, value]) => {
        if (value == null) {
          return;
        }

        if (typeof value === 'string' && value.trim() === '') {
          return;
        }

        if (
          value === EXTRA_QUESTIONNAIRE_DETAILS_FR_TEMPLATE ||
          value === EXTRA_QUESTIONNAIRE_DETAILS_EN_TEMPLATE
        ) {
          return;
        }

        healthAnswers[prop] = value;
      });
    });

    return healthAnswers;
  }

  isSectionCompleted(): boolean {
    return this.isCompleted && this.form.valid;
  }

  getFormGroup(index: number): FormGroup {
    return this.form.controls[index] as FormGroup;
  }

  getFormControl(index: number, controlName: string): FormControl {
    return this.form.controls[index].get(controlName) as FormControl;
  }

  private buildForm(extraQuestionnaires: ExtraQuestionnaire[]) {
    this.form.controls = [];
    extraQuestionnaires.forEach((eq) => {
      const formGroup = this.fb.group<QuestionnaireForm>({});
      eq.questions.forEach((q) => {
        const validatorsArray = [validators.notEmpty];
        if (eq.id === QuestionnaireId.DETAILS) {
          validatorsArray.push(hasNoInputPrompts);
        }
        formGroup.addControl(q.answer.propertyName, this.fb.control(q.answer.value, validatorsArray), {
          emitEvent: false,
        });
      });
      this.form.push(formGroup, {emitEvent: false});
    });
  }

  private updateForm(extraQuestionnaires: ExtraQuestionnaire[]) {
    extraQuestionnaires.forEach((eq, i) => {
      const formGroup = this.form.at(i);
      eq.questions.forEach((q) => {
        const propertyName = q.answer.propertyName;
        const answerValue = q.answer.value;

        if (formGroup.get(propertyName)) {
          formGroup.get(propertyName)!.patchValue(answerValue, {emitEvent: false});
        } else {
          const control = this.fb.control(answerValue, [validators.notEmpty]);
          formGroup.addControl(propertyName, control, {emitEvent: false});
        }
      });
    });
  }

  private createForm(): FormArray<FormGroup<QuestionnaireForm>> {
    const form = this.fb.array<FormGroup<QuestionnaireForm>>([]);

    form.valueChanges
      .pipe(distinctUntilChanged(), debounceTime(DEBOUNCE_TIME), untilDestroyed(this))
      .subscribe((_) => {
        this.propsChange.emit({output: {...this.formToOutput()}});
      });

    return form;
  }

  private areNewAndOldExtraQuestionnairesIdentical(
    newExtraQuestionnaires: ExtraQuestionnaire[],
    oldExtraQuestionnaires: ExtraQuestionnaire[],
  ): boolean {
    return newExtraQuestionnaires.every((eq, index) =>
      areNewAndOldQuestionsIdentical(eq.questions, oldExtraQuestionnaires[index].questions),
    );
  }
}
